/**
 * Utility functions for working with NFTs
 */

/**
 * Safely groups NFTs by their collection (token_id)
 * @param {Array} nfts - Array of NFT objects with token_id property
 * @param {Object} existingCollections - Optional existing collections to merge with
 * @returns {Object} Grouped NFTs by collection
 */
export const groupNftsByCollection = (nfts, existingCollections = {}) => {
  if (!nfts || !Array.isArray(nfts) || nfts.length === 0) {
    return existingCollections;
  }

  try {
    // Start with the existing collections (which now have proper names from token info)
    const collections = { ...existingCollections };
    console.log('Starting groupNftsByCollection with existing collections:', collections);
    
    // First pass: Add NFTs to collections
    nfts.forEach(nft => {
      try {
        const tokenId = nft?.token_id;
        
        if (!tokenId) return;
        
        if (!collections[tokenId]) {
          // Initialize new collection
          collections[tokenId] = {
            collectionId: tokenId,
            name: getCollectionName(nft),
            count: 0,
            nfts: []
          };
        } else if (nft.metadata) {
          // If this NFT has metadata and the collection name is still using token_id format,
          // try to update the collection name with better info
          const currentName = collections[tokenId].name;
          if (currentName === `Collection ${tokenId}`) {
            const betterName = getCollectionName(nft);
            if (betterName !== currentName) {
              collections[tokenId].name = betterName;
            }
          }
        }
        // Note: We no longer try to update collection names here since they should come from token info
        
        // Check if this NFT is already in the collection
        const serialNumber = nft.serial_number;
        const isDuplicate = collections[tokenId].nfts.some(
          existingNft => existingNft.serial_number === serialNumber
        );
        
        if (!isDuplicate) {
          // Add NFT to collection
          collections[tokenId].nfts.push(nft);
          collections[tokenId].count = collections[tokenId].nfts.length;
        }
      } catch (err) {
        console.error('Error processing NFT for collection grouping:', err);
      }
    });

    return collections;
  } catch (err) {
    console.error('Error grouping NFTs by collection:', err);
    return existingCollections;
  }
};

/**
 * Gets a better collection name from an NFT
 * @param {Object} nft - NFT object
 * @returns {string} Collection name
 */
const getCollectionName = (nft) => {
  try {
    // Check various metadata locations for collection name
    if (nft?.metadata?.collection?.name) {
      return nft.metadata.collection.name;
    }
    
    // Check for name property in metadata (often indicates collection name)
    if (nft?.metadata?.name) {
      // If name contains "#" or number, it's likely an item number, not collection name
      // Example: "HashPack #123" -> extract "HashPack" as collection name
      const nameParts = nft.metadata.name.split('#');
      if (nameParts.length > 1) {
        return nameParts[0].trim();
      }
      
      // Check if it's a numbered format like "Collection Name 123"
      const nameMatch = nft.metadata.name.match(/^(.+?)(?:\s+\d+|\s*#\d+)?$/);
      if (nameMatch) {
        return nameMatch[1].trim();
      }
      
      return nft.metadata.name;
    }
    
    // Try to get collection name from symbol if available
    if (nft?.metadata?.symbol) {
      return nft.metadata.symbol;
    }
    
    // Fallback: Use token ID as name
    return `Collection ${nft.token_id}`;
  } catch (err) {
    console.error('Error getting collection name:', err);
    return `Collection ${nft?.token_id || 'Unknown'}`;
  }
}; 